import { EmptyValue } from '@/components/common/EmptyValue';
import { cn } from '@/lib/utils';
import { TABLE_HEADER_CLASS } from '@/styles/dataTableBg';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import {
  DETAIL_TABLE_CELL_CLASS,
  DETAIL_TABLE_FIELD_CELL_CLASS,
  DETAIL_TABLE_HEADER_CELL_CLASS,
  INSTITUTE_VALUE_FIELDS,
  REMOVAL_REASON_LABELS,
  REMOVAL_REQUEST_DISPLAY,
  SUBMISSION_TYPE_TONE,
} from '../constants/instituteSubmission.constants';
import type { RemovalSubmissionDetail } from '../types/instituteSubmission.types';
import { toInstituteDisplayValue } from '../utils/instituteValues';
import { ExpandableComment } from '@/components/common/ExpandableComment';

interface InstituteRemovalRequestSectionProps {
  submission: RemovalSubmissionDetail;
}

export function InstituteRemovalRequestSection({
  submission,
}: InstituteRemovalRequestSectionProps) {
  const { title, icon: RequestIcon } = REMOVAL_REQUEST_DISPLAY[submission.submissionType];
  const detailRows = [
    { key: 'code', label: 'Code', value: submission.code },
    ...INSTITUTE_VALUE_FIELDS.map(({ key, label }) => ({
      key,
      label,
      value: toInstituteDisplayValue(submission.current, key),
    })),
  ];

  return (
    <section className="flex flex-col gap-3">
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg px-3 py-2 text-sm',
          STATUS_COLOR_CLASS[SUBMISSION_TYPE_TONE[submission.submissionType]],
        )}
      >
        <RequestIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p>
            <span className="font-semibold">{title}</span>
            {submission.reasonCode && (
              <span className="font-medium"> · {REMOVAL_REASON_LABELS[submission.reasonCode]}</span>
            )}
          </p>
          {submission.reasonComment && (
            <div className="mt-1 rounded-md border-l-2 border-current bg-card/70 px-2.5 py-1.5">
              <ExpandableComment
                comment={submission.reasonComment}
                shouldKeepLineBreaks
                className="text-foreground"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Current details</h3>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className={TABLE_HEADER_CLASS}>
              <tr>
                <th scope="col" className={DETAIL_TABLE_HEADER_CELL_CLASS}>
                  Field
                </th>
                <th scope="col" className={DETAIL_TABLE_HEADER_CELL_CLASS}>
                  On file
                </th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((detailRow) => (
                <tr key={detailRow.key} className="border-b last:border-b-0">
                  <th
                    scope="row"
                    className={cn(
                      DETAIL_TABLE_CELL_CLASS,
                      DETAIL_TABLE_FIELD_CELL_CLASS,
                      'w-1/4 font-medium text-muted-foreground',
                    )}
                  >
                    {detailRow.label}
                  </th>
                  <td className={DETAIL_TABLE_CELL_CLASS}>{detailRow.value ?? <EmptyValue />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
